
import {of as observableOf,  Observable } from 'rxjs';

import {catchError } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@environments/environment';
import { AddUser, UpdateUser } from '@routes/usersteams/models/user-model';
import { ErrorResponse, SuccessResponse } from '@shared/services/api/legacy/response';
import { IPasswordSettings, IProfileSettings } from '@shared/services/api/legacy/settings-service/setting-model';
import { ITenants, ITokenRefresh, ITokenResult, IUserDetails, IUsers, IUsersForDropdown } from '@shared/services/api/legacy/users-service/user-model';
import { Store } from '@ngrx/store';
import { IState } from '@state/state.module';

@Injectable()
export class UsersService {
    constructor(private http: HttpClient, private store: Store<IState>) { }

    private static get URL(): string { return `${environment.api.baseUrl}${environment.api.prefix}${UsersService.ENDPOINT}`; }
    private static readonly ENDPOINT = '/users';

    public refresh(): Observable<ITokenRefresh | ErrorResponse> {
        return this.http
            .get<ITokenRefresh>(UsersService.URL + '/refresh').pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }

    public exchangeTokenForTenant(tenantId: string): Observable<ITokenResult | ErrorResponse> {
        return this.http
            .post<ITokenResult>(UsersService.URL + '/exchange-token/' + tenantId, {}).pipe(
             catchError(error => observableOf(new ErrorResponse(error.error))));
    }

    public getUser(id: string): Observable<SuccessResponse<IUserDetails> | ErrorResponse> {
        return this.http
            .get<SuccessResponse<IUserDetails>>(UsersService.URL + '/' + id).pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }

    public getPrincipalUserProfile(): Observable<SuccessResponse<IProfileSettings> | ErrorResponse> {
        return this.http
            .get<SuccessResponse<IProfileSettings>>(UsersService.URL + '/principal/profile?product=invitedesk').pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }

    public getUsers(): Observable<SuccessResponse<IUsers> | ErrorResponse> {
        return this.http
            .get<SuccessResponse<IUsers>>(UsersService.URL).pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }

    public getUsersTeamRights(eventId: string): Observable<SuccessResponse<IUsers> | ErrorResponse> {
        return this.http
            .get<SuccessResponse<IUsers>>(UsersService.URL + '/' + eventId  + '/teamrights').pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }

    public getUsersInternal(): Observable<SuccessResponse<IUsers> | ErrorResponse> {
        return this.http
            .get<SuccessResponse<IUsers>>(UsersService.URL + '/internal').pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }

    public getUsersForDropdown(): Observable<SuccessResponse<IUsersForDropdown> | ErrorResponse>  {
        return this.http
            .get<SuccessResponse<IUsersForDropdown>>(UsersService.URL + '/dropdown').pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }

    public getUsersTeamRightsForDropdown(eventId: string): Observable<SuccessResponse<IUsersForDropdown> | ErrorResponse>  {
        return this.http
            .get<SuccessResponse<IUsersForDropdown>>(UsersService.URL + '/' + eventId  + '/dropdown/teamrights').pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }

    public addUser(role: AddUser): Observable<SuccessResponse<boolean> | ErrorResponse> {
        return this.http
            .post<SuccessResponse<boolean>>(UsersService.URL, role).pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }

    public updateUser(role: UpdateUser): Observable<SuccessResponse<boolean> | ErrorResponse> {
        return this.http
            .put<SuccessResponse<boolean>>(UsersService.URL, role).pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }

    public setPrincipalUserProfile(profile: IProfileSettings): Observable<SuccessResponse<boolean> | ErrorResponse> {
        return this.http
            .patch<SuccessResponse<boolean>>(UsersService.URL + '/principal/profile', profile).pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }

    public enableUser(id: string): Observable<SuccessResponse<boolean> | ErrorResponse> {
        return this.http
            .patch<SuccessResponse<boolean>>(UsersService.URL + '/' + id + '/enable', null).pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }

    public disableUser(id: string): Observable<SuccessResponse<boolean> | ErrorResponse> {
        return this.http
            .patch<SuccessResponse<boolean>>(UsersService.URL + '/' + id + '/disable', null).pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }

    public anonimizeUser(id: string): Observable<SuccessResponse<boolean> | ErrorResponse> {
        return this.http
            .patch<SuccessResponse<boolean>>(UsersService.URL + '/' + id + '/anonimize', null).pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }

    public resendInvitationUser(id: string): Observable<SuccessResponse<boolean> | ErrorResponse> {
        return this.http
            .post<SuccessResponse<boolean>>(UsersService.URL + '/' + id + '/resend', null).pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }

    public changePrincipalUserPassword(model: IPasswordSettings): Observable<SuccessResponse<boolean> | ErrorResponse> {
        return this.http
            .post<SuccessResponse<boolean>>(UsersService.URL + '/principal/changepassword', model).pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }

    public getTenants(): Observable<SuccessResponse<ITenants> | ErrorResponse> {
        return this.http
            .get<SuccessResponse<ITenants>>(UsersService.URL + '/tenants').pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }
}
